import {
  getLegacyQueryLanguage,
  getPathLanguagePrefix,
  stripLanguagePrefix,
  withLanguagePath,
} from './url-language-utils'

const RETURN_TARGET_BASE = 'https://return-target.mentiss.invalid'

export function returnTargetFrom(location: {
  pathname: string
  search?: string
  hash?: string
}): string {
  return `${stripLanguagePrefix(location.pathname)}${location.search ?? ''}${location.hash ?? ''}`
}

export function signInUrlReturningTo(target: string): string {
  return `/signin?redirect=${encodeURIComponent(target)}`
}

export function signUpUrlReturningTo(target: string): string {
  return `/signup?redirect=${encodeURIComponent(target)}`
}

export function verifyEmailUrlReturningTo(target: string): string {
  return `/verify-email?redirect=${encodeURIComponent(safeReturnTarget(target, '/new'))}`
}

export function forgetPasswordUrlReturningTo(target: string): string {
  return `/forget-password?redirect=${encodeURIComponent(target)}`
}

export function safeReturnTarget(
  raw: string | null | undefined,
  fallback = '/',
): string {
  if (!raw)
    return fallback
  if (!raw.startsWith('/'))
    return fallback
  if (
    /^\/(?:\/|\\|%2f|%5c)/i.test(raw)
    || [...raw].some((char) => {
      const point = char.codePointAt(0) ?? 0
      return char === '\\' || point < 32 || point === 127
    })
  ) {
    return fallback
  }
  try {
    const parsed = new URL(raw, RETURN_TARGET_BASE)
    if (parsed.origin !== RETURN_TARGET_BASE)
      return fallback
  }
  catch {
    return fallback
  }
  return raw
}

export function returnTargetNavigation(
  raw: string | null | undefined,
  fallback: string,
  currentPathname: string,
): { to: string, fullNavigation: boolean } {
  const target = safeReturnTarget(raw, fallback)
  const targetPathname = target.split(/[?#]/, 1)[0] || '/'
  const targetPrefix = getPathLanguagePrefix(targetPathname)
  if (!targetPrefix)
    return { to: target, fullNavigation: false }

  if (targetPrefix !== getPathLanguagePrefix(currentPathname))
    return { to: target, fullNavigation: true }

  const relative = target.slice(targetPrefix.length)
  return {
    to: relative.startsWith('/') ? relative : `/${relative}`,
    fullNavigation: false,
  }
}

export function passwordResetRedirectTo(
  location: { origin: string, pathname: string, search: string },
  rawReturnTarget: string | null | undefined,
): string {
  const prefix = getPathLanguagePrefix(location.pathname)
  const legacyLanguage = prefix ? null : getLegacyQueryLanguage(location.search)
  const pathname = prefix
    ? `${prefix}/reset-password`
    : legacyLanguage
      ? withLanguagePath('/reset-password', legacyLanguage)
      : '/reset-password'
  const url = new URL(pathname, location.origin)
  if (rawReturnTarget)
    url.searchParams.set('redirect', safeReturnTarget(rawReturnTarget))
  return url.toString()
}
